import * as THREE from "three";
import { defineModel, styleV3, type Part } from "../../lib";

// An open WWI touring staff car: field-grey body, two bench seats in an open
// cockpit, the canvas top folded into a roll at the rear, a spare wheel
// strapped to the left side and a small red pennant flying from the right
// front fender. Front is +z, all inside one tile.

const { box, cyl } = styleV3;

const GREY = "#6d6f64";
const GUNMETAL = "#4a4d52";
const CANVAS = "#cbbd93";
const RED = "#c0392b";
const TYRE = "#33342f";
const KHAKI = "#7a6f4e";

function wheel(x: number, z: number, r: number): Part[] {
  return [
    cyl(r, r, 0.05, 12, TYRE, [x, r, z], [0, 0, Math.PI / 2]),
    box(0.065, r * 1.6, 0.024, KHAKI, [x, r, z], [Math.PI / 4, 0, 0]),
    box(0.065, r * 1.6, 0.024, KHAKI, [x, r, z], [-Math.PI / 4, 0, 0]),
    cyl(r * 0.28, r * 0.28, 0.075, 8, GUNMETAL, [x, r, z], [0, 0, Math.PI / 2]),
  ];
}

function parts(): Part[] {
  const R = 0.1;
  const out: Part[] = [
    ...wheel(-0.19, 0.27, R),
    ...wheel(0.19, 0.27, R),
    ...wheel(-0.19, -0.27, R),
    ...wheel(0.19, -0.27, R),
    cyl(0.016, 0.016, 0.38, 6, GUNMETAL, [0, R, 0.27], [0, 0, Math.PI / 2]),
    cyl(0.016, 0.016, 0.38, 6, GUNMETAL, [0, R, -0.27], [0, 0, Math.PI / 2]),
    box(0.26, 0.04, 0.7, GUNMETAL, [0, 0.14, 0]),

    // -- Field-grey body: open tub plus bonnet and radiator -------------------
    box(0.36, 0.16, 0.52, GREY, [0, 0.24, -0.1]),
    box(0.3, 0.14, 0.28, GREY, [0, 0.25, 0.28]),
    box(0.32, 0.16, 0.03, GUNMETAL, [0, 0.24, 0.43]),

    // fenders and running boards
    box(0.06, 0.025, 0.24, GREY, [-0.19, 0.235, 0.27]),
    box(0.06, 0.025, 0.24, GREY, [0.19, 0.235, 0.27]),
    box(0.06, 0.025, 0.24, GREY, [-0.19, 0.235, -0.27]),
    box(0.06, 0.025, 0.24, GREY, [0.19, 0.235, -0.27]),
    box(0.05, 0.02, 0.26, GREY, [-0.2, 0.16, 0]),
    box(0.05, 0.02, 0.26, GREY, [0.2, 0.16, 0]),

    // -- Open cockpit: two bench seats and a steering wheel -------------------
    box(0.3, 0.05, 0.1, TYRE, [0, 0.34, 0.0]),
    box(0.3, 0.1, 0.03, TYRE, [0, 0.37, -0.06]),
    box(0.3, 0.05, 0.1, TYRE, [0, 0.34, -0.21]),
    box(0.3, 0.1, 0.03, TYRE, [0, 0.38, -0.28]),
    cyl(0.01, 0.01, 0.14, 6, GUNMETAL, [0.09, 0.35, 0.1], [0.9, 0, 0]),
    cyl(0.04, 0.04, 0.015, 10, TYRE, [0.09, 0.4, 0.055], [0.9, 0, 0]),

    // -- Folded canvas top: a roll across the rear deck ------------------------
    cyl(0.055, 0.055, 0.32, 8, CANVAS, [0, 0.36, -0.33], [0, 0, Math.PI / 2]),

    // -- Spare wheel strapped to the left side of the tub ----------------------
    cyl(R, R, 0.045, 12, TYRE, [-0.19, 0.27, -0.05], [0, 0, Math.PI / 2]),
    cyl(0.03, 0.03, 0.055, 8, GUNMETAL, [-0.19, 0.27, -0.05], [0, 0, Math.PI / 2]),

    // -- Red pennant on the right front fender --------------------------------
    cyl(0.005, 0.005, 0.14, 4, GUNMETAL, [0.19, 0.315, 0.36]),
    {
      geometry: new THREE.ConeGeometry(0.026, 0.08, 4),
      color: RED,
      position: [0.19, 0.375, 0.315],
      rotation: [-Math.PI / 2, 0, 0],
      scale: [0.3, 1, 1],
    },
  ];
  return out;
}

export const staffCar = defineModel({
  id: "staff-car",
  name: "Staff Car",
  batchId: "wwi",
  tags: ["vehicle", "wwi", "military", "car", "touring", "officer"],
  summary: "An open field-grey touring car with a folded top and a red pennant.",
  description:
    "A WWI staff car: an open field-grey touring body with two bench seats, " +
    "the canvas top folded into a roll at the rear, a spare wheel on the " +
    "left side and a small red pennant flying from the right front fender.",
  author: "Fable 5",
  footprint: 0.5,
  tiles: { w: 1, h: 1 },
  parts,
  animations: [],
});
